/**
 * Compliance Templates Page
 *
 * Browse and preview compliance document templates from ComplianceDocumentLibrarian.
 * Supports filtering by framework (SOC2, ISO27001, GDPR, CCPA) and category.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Alert,
  Spinner,
  Badge,
  Select,
  TextInput
} from 'flowbite-react';
import {
  HiDocument,
  HiSearch,
  HiFilter,
  HiShieldCheck,
  HiDocumentText
} from 'react-icons/hi';
import { Make_Authorized_API_Call } from '../services/api/utils';
import { API_ENDPOINTS } from '../services/api/endpoints';
import TemplateCard from '../components/templates/TemplateCard';

interface ComplianceTemplate {
  Template_ID: string;
  Title: string;
  Category: string;
  Type: string;
  Compliance_Frameworks: string[];
  Source_File: string;
  Description: string;
}

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<ComplianceTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [frameworkFilter, setFrameworkFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Framework options
  const frameworks = ['SOC2', 'ISO27001', 'GDPR', 'CCPA'];

  // Category options
  const categories = [
    { value: 'policies', label: 'Policies' },
    { value: 'procedures', label: 'Procedures' },
    { value: 'controls', label: 'Controls' },
    { value: 'assessments', label: 'Assessments' },
    { value: 'evidence', label: 'Evidence' }
  ];

  // Fetch templates
  useEffect(() => {
    fetchTemplates();
  }, []);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [templates, frameworkFilter, categoryFilter, searchQuery]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await Make_Authorized_API_Call<ComplianceTemplate[]>(
        API_ENDPOINTS.COMPLIANCE_TEMPLATES.LIST,
        'GET'
      );

      // API response structure: result.data = { success, message, data: { Records: [...] }, meta }
      const apiResponse = result.data as any;
      if (result.success && apiResponse?.data?.Records && Array.isArray(apiResponse.data.Records)) {
        setTemplates(apiResponse.data.Records);
      } else {
        throw new Error(apiResponse?.message || result.message || 'Failed to fetch templates');
      }
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...templates];

    // Apply framework filter
    if (frameworkFilter !== 'all') {
      filtered = filtered.filter(template =>
        template.Compliance_Frameworks.includes(frameworkFilter)
      );
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(template =>
        template.Category === categoryFilter
      );
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(template =>
        template.Title.toLowerCase().includes(query) ||
        template.Description.toLowerCase().includes(query) ||
        template.Type.toLowerCase().includes(query)
      );
    }

    setFilteredTemplates(filtered);
  };

  const handleTemplateSelect = (templateId: string) => {
    navigate(`/templates/${templateId}`);
  };

  const handleUseTemplate = (templateId: string) => {
    // Navigate to document editor with template pre-selected
    navigate(`/documents/new?template=${templateId}`);
  };

  // Group templates by category
  const templatesByCategory = filteredTemplates.reduce((acc, template) => {
    if (!acc[template.Category]) {
      acc[template.Category] = [];
    }
    acc[template.Category].push(template);
    return acc;
  }, {} as Record<string, ComplianceTemplate[]>);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <HiDocumentText className="text-3xl text-blue-600" />
          <h1 className="text-3xl font-bold">Compliance Templates</h1>
        </div>
        <p className="text-gray-600">
          Browse and use pre-built compliance document templates for SOC2, ISO27001, GDPR, and CCPA
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <TextInput
              icon={HiSearch}
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Framework Filter */}
          <div>
            <Select
              value={frameworkFilter}
              onChange={(e) => setFrameworkFilter(e.target.value)}
            >
              <option value="all">All Frameworks</option>
              {frameworks.map(framework => (
                <option key={framework} value={framework}>
                  {framework}
                </option>
              ))}
            </Select>
          </div>

          {/* Category Filter */}
          <div>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/* Active Filters Display */}
        {(frameworkFilter !== 'all' || categoryFilter !== 'all' || searchQuery) && (
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-sm text-gray-600">Active filters:</span>
            {frameworkFilter !== 'all' && (
              <Badge color="blue" onClick={() => setFrameworkFilter('all')} className="cursor-pointer">
                {frameworkFilter} ✕
              </Badge>
            )}
            {categoryFilter !== 'all' && (
              <Badge color="purple" onClick={() => setCategoryFilter('all')} className="cursor-pointer">
                {categories.find(c => c.value === categoryFilter)?.label} ✕
              </Badge>
            )}
            {searchQuery && (
              <Badge color="gray" onClick={() => setSearchQuery('')} className="cursor-pointer">
                "{searchQuery}" ✕
              </Badge>
            )}
          </div>
        )}
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Spinner size="xl" />
          <span className="ml-3">Loading templates...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Alert color="failure">
          <span className="font-medium">Error:</span> {error}
        </Alert>
      )}

      {/* Templates Display */}
      {!loading && !error && (
        <>
          {/* Summary */}
          <div className="mb-6">
            <p className="text-gray-600">
              Showing {filteredTemplates.length} of {templates.length} templates
            </p>
          </div>

          {/* Templates by Category */}
          {Object.keys(templatesByCategory).length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <HiDocument className="mx-auto text-6xl text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No templates found</h3>
                <p className="text-gray-600 mb-4">
                  Try adjusting your filters or search query
                </p>
                <Button
                  onClick={() => {
                    setFrameworkFilter('all');
                    setCategoryFilter('all');
                    setSearchQuery('');
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </Card>
          ) : (
            Object.entries(templatesByCategory).map(([category, categoryTemplates]) => (
              <div key={category} className="mb-8">
                <h2 className="text-2xl font-bold mb-4 capitalize flex items-center gap-2">
                  <HiShieldCheck className="text-blue-600" />
                  {category}
                  <Badge color="gray">{categoryTemplates.length}</Badge>
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {categoryTemplates.map(template => (
                    <TemplateCard
                      key={template.Template_ID}
                      template={template}
                      onSelect={() => handleTemplateSelect(template.Template_ID)}
                      onUse={() => handleUseTemplate(template.Template_ID)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default TemplatesPage;
